/**
 * Communication schemas — messaging threads, in-app notifications.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type { ThreadCategoryCode as FhirThreadCategory } from '@unify/fhir-model/resources/communication';

/** Thread category codes — synced with fhir-model (includes legacy values). */
export const threadCategoryValues = [
  'client-thread',
  'group-thread',
  'provider-thread',
  'client-conversation',
  'provider-conversation',
  'space',
  'system',
] as const satisfies readonly FhirThreadCategory[];

/**
 * Create a new messaging thread (thread root Communication).
 */
export const createThreadSchema = z.object({
  senderReference: z.string().min(1, 'Sender is required'),
  senderDisplay: z.string().optional(),
  recipientReferences: z
    .array(z.string().min(1))
    .min(1, 'At least one recipient is required'),
  category: z.enum(threadCategoryValues),
  topicText: z.string().optional(),
  initialMessageText: z.string().min(1, 'Message content is required'),
});

export type CreateThreadInput = z.infer<typeof createThreadSchema>;

/**
 * Send a message within an existing thread.
 */
export const sendMessageSchema = z.object({
  threadId: z.string().min(1, 'Thread is required'),
  senderReference: z.string().min(1, 'Sender is required'),
  senderDisplay: z.string().optional(),
  text: z.string().min(1, 'Message cannot be empty'),
  quotedMessageId: z.string().optional(),
  quotedSender: z.string().optional(),
  quotedContent: z.string().optional(),
});

export type SendMessageInput = z.infer<typeof sendMessageSchema>;

/**
 * In-app notification Communication creation (rules engine / system).
 */
export const inAppNotificationSchema = z.object({
  recipientReference: z.string().min(1, 'Recipient is required'),
  senderReference: z.string().optional(),
  subject: z.string().optional(),
  payloadText: z.string().min(1, 'Notification content is required'),
  category: z.string().optional(),
});

export type InAppNotificationInput = z.infer<typeof inAppNotificationSchema>;
